import React, { type ComponentPropsWithoutRef, type ComponentRef } from 'react'

import * as ToastPrimitives from '@radix-ui/react-toast'
import { cva, type VariantProps } from 'class-variance-authority'

import { Icons } from '@/components/atoms/Icons/Icons.tsx'
import { cn } from '@/lib/utils.ts'

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = React.forwardRef<
  ComponentRef<typeof ToastPrimitives.Viewport>,
  ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'fixed right-0 bottom-0 z-2147483647 m-0 flex w-[385px] max-w-screen list-none flex-col gap-2.5 p-(--viewport-padding) outline-hidden [--viewport-padding:15px]',
      className
    )}
    {...props}
  />
))

const toastVariants = cva(
  "group relative grid grid-cols-[auto_max-content] items-center gap-x-[15px] rounded-md bg-white p-[15px] shadow-xl [grid-template-areas:'title_action'_'description_action'] data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-(--radix-toast-swipe-end-x) data-[swipe=move]:translate-x-(--radix-toast-swipe-move-x) data-[swipe=cancel]:transition-[transform_200ms_ease-out] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full sm:data-[state=open]:slide-in-from-bottom-full",
  {
    variants: {
      variant: {
        default: 'border bg-white text-gray-800',
        destructive: 'destructive border-red-700 bg-red-700 text-white',
        alert: 'border-2 border-[#9b1c1c]',
        teamMessage: 'border-2 border-[#16bdca] bg-[#edfafa] text-gray-800',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

const Toast = React.forwardRef<
  ComponentRef<typeof ToastPrimitives.Root>,
  ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => (
  <ToastPrimitives.Root ref={ref} className={cn(toastVariants({ variant }), className)} {...props} />
))

const ToastClose = React.forwardRef<
  ComponentRef<typeof ToastPrimitives.Close>,
  ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      'rounded-allocated absolute top-1 right-1 p-1 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 focus:ring-1 focus:outline-hidden focus:group-[.destructive]:ring-red-600 focus:group-[.destructive]:ring-offset-red-700',
      className
    )}
    toast-close=""
    {...props}>
    <Icons.close className="size-4 fill-black group-[.destructive]:fill-white" />
  </ToastPrimitives.Close>
))

const ToastTitle = React.forwardRef<
  ComponentRef<typeof ToastPrimitives.Title>,
  ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title ref={ref} className={cn('[grid-area:title]', className)} {...props} />
))

const ToastDescription = React.forwardRef<
  ComponentRef<typeof ToastPrimitives.Description>,
  ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn('m-0 text-[14px] leading-[1.3] [grid-area:description]', className)}
    {...props}
  />
))

const ToastAction = React.forwardRef<
  ComponentRef<typeof ToastPrimitives.Action>,
  ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action ref={ref} className={cn('[grid-area:action]', className)} {...props} />
))

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast> & VariantProps<typeof toastVariants>

export { Toast, ToastAction, ToastClose, ToastDescription, type ToastProps, ToastProvider, ToastTitle, ToastViewport }
